import { useState, useCallback } from 'react';

interface UseApiMutationOptions<TResult> {
  /** Called after a successful mutation with the result value. */
  onSuccess?: (result: TResult) => void;
  /** Message to set on the success flag (truthy string or boolean). Not displayed automatically — the caller decides how to surface it. */
  successMessage?: string;
  /** Fallback message when the caught error has no `.message`. */
  errorMessage?: string;
}

/**
 * Wraps an async mutation function with loading, error, and success state.
 *
 * This hook does NOT use a toast library because the codebase currently
 * handles success/error display inline (banners, badges, etc.) rather
 * than via toasts. Callers can use the returned `error` and `success`
 * values to render feedback however they like.
 */
export function useApiMutation<TArgs extends unknown[], TResult = void>(
  mutationFn: (...args: TArgs) => Promise<TResult>,
  options?: UseApiMutationOptions<TResult>,
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const mutate = useCallback(
    async (...args: TArgs): Promise<TResult | undefined> => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(false);
        const result = await mutationFn(...args);
        setSuccess(true);
        options?.onSuccess?.(result);
        return result;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : (options?.errorMessage ?? 'An error occurred');
        setError(message);
        console.error(err);
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [mutationFn, options],
  );

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setSuccess(false);
  }, []);

  return { mutate, loading, error, success, reset };
}
